import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase.js'
import { getImpersonationState, getAdminSession, clearImpersonation } from '../../lib/impersonation.js'
import { useGameStore } from '../../stores/gameStore.js'

export default function ImpersonationBanner() {
  const navigate = useNavigate()
  const reset = useGameStore(s => s.reset)
  const [state, setState] = useState(() => getImpersonationState())
  const [collapsed, setCollapsed] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    function onUpdate() { setState(getImpersonationState()) }
    window.addEventListener('kart_impersonation_change', onUpdate)
    window.addEventListener('storage', onUpdate)
    return () => {
      window.removeEventListener('kart_impersonation_change', onUpdate)
      window.removeEventListener('storage', onUpdate)
    }
  }, [])

  const hasState = !!state
  useEffect(() => {
    if (!hasState) return
    timerRef.current = setTimeout(() => setCollapsed(true), 2000)
    return () => clearTimeout(timerRef.current)
  }, [hasState])

  if (!state) return null

  function expand() {
    clearTimeout(timerRef.current)
    setCollapsed(false)
  }

  function scheduleCollapse() {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCollapsed(true), 2000)
  }

  async function handleRestore() {
    const adminSession = getAdminSession()
    if (adminSession) {
      await supabase.auth.setSession(adminSession)
    }
    clearImpersonation()
    reset()
    navigate('/admin/users', { replace: true })
  }

  return (
    <div
      onMouseEnter={expand}
      onMouseLeave={scheduleCollapse}
      className="fixed bottom-0 left-0 z-[9999] bg-orange-500 text-white shadow-lg overflow-hidden"
      style={{
        width: collapsed ? '40px' : '100vw',
        height: '44px',
        borderTopRightRadius: collapsed ? '8px' : '0',
        transition: 'width 300ms ease-in-out, border-top-right-radius 300ms ease-in-out',
      }}
    >
      {/* Icône œil — visible uniquement réduit */}
      <div
        className="absolute inset-y-0 left-0 w-10 flex items-center justify-center"
        style={{ opacity: collapsed ? 1 : 0, transition: 'opacity 200ms ease-in-out' }}
      >
        <span className="text-xl select-none">👁</span>
      </div>

      {/* Barre complète — visible uniquement étendu */}
      <div
        className="flex items-center justify-between px-4 gap-4 h-full text-sm"
        style={{
          opacity: collapsed ? 0 : 1,
          pointerEvents: collapsed ? 'none' : 'auto',
          transition: 'opacity 200ms ease-in-out',
        }}
      >
        <span className="truncate min-w-0">
          👁 Vous consultez l'application en tant que <strong>{state.targetEmail}</strong>
        </span>
        <button
          onClick={handleRestore}
          className="shrink-0 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg font-medium transition whitespace-nowrap"
        >
          Reprendre ma session
        </button>
      </div>
    </div>
  )
}
